import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { TooltipPanel } from './TooltipPanel.component';

type StatProps = {
  label: string;
  value: string;
  iconName: React.ComponentProps<typeof Icon>['name'];
  danger?: boolean;
  tooltip?: string;
};

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <Layout.Row
      style={{ position: 'relative' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <Icon name="information-circle-outline" size={12} color={theme.colors.secondary} />
      {visible ? <TooltipPanel text={text} /> : null}
    </Layout.Row>
  );
};

const Stat: React.FC<StatProps> = ({ label, value, iconName, danger, tooltip }) => {
  const accent = danger ? theme.colors.destructive : theme.colors.brandPrimary;
  return (
    <Layout.Column
      grow
      bg={danger ? 'destructiveXLight' : 'white'}
      border={[1, 'solid', danger ? 'destructive' : 'inputBorder']}
      radius={16}
      py={14}
      px={16}
    >
      <Layout.Row align>
        <Icon name={iconName} size={14} color={accent} />
        <Spacer.Horizontal size={6} />
        <OpenSans.Primary size="xs-12" color={danger ? 'destructive' : 'secondary'}>
          {label}
        </OpenSans.Primary>
        {tooltip ? (
          <>
            <Spacer.Horizontal size={4} />
            <InfoTooltip text={tooltip} />
          </>
        ) : null}
      </Layout.Row>
      <Spacer.Vertical size={6} />
      <OpenSans.Primary size="m-18" weight="bold-700" color={danger ? 'destructive' : 'primary'}>
        {value}
      </OpenSans.Primary>
    </Layout.Column>
  );
};

export const StatStrip: React.FC<{
  seatsUsed: number;
  seatsAllowed: number;
  planLabel: string;
  nextBillingLabel: string;
  perPeriodLabel: string;
  perPeriodAmount: string;
}> = ({
  seatsUsed,
  seatsAllowed,
  planLabel,
  nextBillingLabel,
  perPeriodLabel,
  perPeriodAmount,
}) => {
  const overSeats = !!seatsAllowed && seatsUsed > seatsAllowed;

  return (
    <Layout.Row style={{ gap: 12 }}>
      <Stat
        label={getTranslation('Seats Used')}
        value={`${seatsUsed} / ${seatsAllowed}`}
        iconName="people-outline"
        danger={overSeats}
        tooltip={getTranslation('Each person counts once, across every workspace on this plan.')}
      />
      <Stat label={getTranslation('Your Plan')} value={planLabel} iconName="star-outline" />
      <Stat
        label={getTranslation('Next Billing')}
        value={nextBillingLabel}
        iconName="calendar-outline"
      />
      <Stat label={perPeriodLabel} value={perPeriodAmount} iconName="cash-outline" />
    </Layout.Row>
  );
};
